import { Invoice } from "src/app/domains";
import { OfferItemGroup } from "src/app/domains/offer-item-group";
import { ObjectId } from "src/app/shared/helpers/objectid.helper";

import { Contract } from "../../customer/domains/contract";
import { Tag } from "../../eva-company/domains/eva-company-tag";

export enum ContractInvoiceStatus {
  TO_INVOICE = "TO_INVOICE",
  INVOICED = "INVOICED",
  PAID = "PAID",
  CANCELED = "CANCELED",
}

/** Nature d'une ligne de « Facturation des contrats » (EP-1151). */
export enum ContractInvoiceNature {
  INSTALLMENT = "INSTALLMENT",
  PASSAGE = "PASSAGE",
  EXCEPTIONAL = "EXCEPTIONAL",
}

export const CONTRACT_INVOICE_NATURE_LABELS: Record<ContractInvoiceNature, string> = {
  [ContractInvoiceNature.INSTALLMENT]: "Échéance",
  [ContractInvoiceNature.PASSAGE]: "Passage",
  [ContractInvoiceNature.EXCEPTIONAL]: "Exceptionnelle",
};

/** Mode de facturation actuel du contrat, affiché en information. */
export enum ContractBillingMode {
  ANTICIPATED = "ANTICIPATED",
  ON_COMPLETION = "ON_COMPLETION",
}

export const CONTRACT_BILLING_MODE_LABELS: Record<ContractBillingMode, string> = {
  [ContractBillingMode.ANTICIPATED]: "Anticipé",
  [ContractBillingMode.ON_COMPLETION]: "À la réalisation",
};

export class ContractInvoice {
  contract_invoice_id: string = ObjectId()();
  contract_id: string = null;
  eva_company_id: string = null;
  renew_date: Date = new Date();
  contract_quotation: OfferItemGroup = null;
  status: ContractInvoiceStatus = ContractInvoiceStatus.TO_INVOICE;
  invoice_id: string = null;
  tags_ids: string[];
  tags: Tag[] = [];

  // External

  contract_corporate_id: string = null;
  contract_name: string = null;
  customer_id: string = null;
  customer_name: string = null;
  invoice_corporate_id: string = null;
  invoice_date: Date = null;
  invoice_sent_on: Date = null;
  invoice_payment_deadline: Date = null;

  // Facturation des contrats unifiée : une ligne peut aussi être un groupe de passages à facturer
  // ou une facture de passages (sans contract_invoice_id).
  row_id: string = null;
  nature: ContractInvoiceNature = ContractInvoiceNature.INSTALLMENT;
  contract_mode: ContractBillingMode = ContractBillingMode.ANTICIPATED;
  contract_disabled = false;
  intervention_ids: string[] = [];
  passages_count: number = 0;
  passages_first_date: Date = null;
  passages_last_date: Date = null;
  invoice_share_ht: number = null;
  invoice_share_ttc: number = null;
  invoice_shared = false;

  invoice: Invoice = null;
  contract: Contract = null;

  get total_ht(): number {
    if (this.invoice_share_ht !== null) return this.invoice_share_ht;
    return this.contract_quotation ? this.contract_quotation.totalSellingPriceWithAuction() : 0;
  }

  /** Date de tri de la colonne « Échéance / passages ». */
  get period_date(): Date {
    return this.renew_date ?? this.passages_first_date ?? this.invoice_date;
  }

  public static createInstance(jsonData: ContractInvoice): ContractInvoice {
    const d = Object.assign(new ContractInvoice(), jsonData);
    // Les lignes à la réalisation n'ont pas de date de renouvellement : ne pas garder le défaut `new Date()`.
    d.renew_date = d.renew_date ? new Date(d.renew_date) : null;
    if (d.contract_quotation) {
      d.contract_quotation = OfferItemGroup.createInstance(d.contract_quotation);
    }
    if (d.invoice_date) {
      d.invoice_date = new Date(d.invoice_date);
    }
    if (d.invoice_sent_on) {
      d.invoice_sent_on = new Date(d.invoice_sent_on);
    }
    for (const field of ["invoice_payment_deadline", "passages_first_date", "passages_last_date"] as const) {
      if (d[field]) d[field] = new Date(d[field]);
    }
    if (d.invoice) {
      d.invoice = Invoice.createInstance(d.invoice);
    }
    if (d.contract) {
      d.contract = Contract.createInstance(d.contract);
    }
    if (d.tags) {
      d.tags = Tag.createInstances(d.tags);
    }
    return d;
  }

  public static createInstances(jsonData: any[]): ContractInvoice[] {
    return jsonData.map(ContractInvoice.createInstance);
  }

  clone(): ContractInvoice {
    let i = Object.assign(new ContractInvoice(), this);
    return i;
  }
}
